import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import { ContentType } from 'recharts/types/component/Tooltip'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import spendBarStack from 'src/components/SpendGraph/SpendBarStack'
import { ExtendCurrency } from 'src/enums'
import { selectBrandColorHighlight } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export enum SpendType {
  none = 'NONE',
  virtual = 'VIRTUAL',
  physical = 'PHYSICAL',
  billPay = 'BILL_PAY',
}
export interface SpendGraphData {
  x: number
  y?: number
  usd?: number
  usdPhysical?: number
  usdBillPay?: number
  cad?: number
  cadPhysical?: number
  cadBillPay?: number
  fill?: string
  fillPhysical?: string
  fillBillPay?: string
  month?: number
  selectedSpendType?: SpendType
  selectedCurrency?: string
}

interface SpendGraphProps {
  className?: string
  currency: ExtendCurrency
  graphData: SpendGraphData[]
  xAxisProps?: {
    label?: string | React.ReactElement
    domain?: [string] | [string, string]
    tickFormatter?: (val: number) => string
    ticks?: boolean
  }
  yAxisProps?: {
    domain?: [number, number]
    tickFormatter?: (val: number) => string
  }
  barProps?: {
    fill?: string
    radius?: number | [number, number, number, number]
    size?: number
  }
  onClick?: (data: unknown) => void
  defs?: React.ReactNode
  tooltip?: ContentType<ValueType, NameType>
  height?: number | string
}

export interface HoverData {
  x: number
  y: number
  width: number
  payload?: SpendGraphData
}

const SpendGraph: React.FC<SpendGraphProps> = (props): React.ReactElement => {
  const brandHighlightColor = useSelector(selectBrandColorHighlight)
  const { analytics } = useAnalyticsContext()

  const [position, setPosition] = useState({
    data: { x: 0, y: 0, width: 0 },
    show: false,
  })
  const [hoverIndex, setHoverIndex] = useState(-1)
  const [spendType, setSelectedSpendType] = useState(SpendType.none)
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    'usd',
  )

  const yDataExists = props.graphData.some((data) => data.y !== undefined)
  const usdDataExists = props.graphData.some((data) => data.usd !== undefined)
  const cadDataExists = props.graphData.some((data) => data.cad !== undefined)

  useEffect(() => {
    const tooltip = document.querySelector(
      '.recharts-tooltip-wrapper',
    ) as HTMLElement
    if (!tooltip) return

    // Init tooltip values
    const tooltipHeight = tooltip.getBoundingClientRect().height
    const tooltipWidth = tooltip.getBoundingClientRect().width

    tooltip.style.setProperty(
      'transform',
      `translate(${position?.data.x + (props.barProps?.size || 0) / 2 - tooltipWidth / 2}px, ${position?.data.y}px)`,
    )
    tooltip.style.setProperty('pointer-events', 'none')
    tooltip.style.setProperty('position', 'absolute')
    tooltip.style.setProperty('top', `-${tooltipHeight}px`)
    if (hoverIndex === 0) {
      tooltip.style.setProperty(
        'left',
        `${tooltipWidth / 2 - position?.data.width / 2 - (usdDataExists && cadDataExists ? 10 : 0)}px`,
      )
    } else {
      tooltip.style.setProperty(
        'left',
        `-${tooltipWidth / 2 - position?.data.width / 2 - (usdDataExists && cadDataExists ? 10 : 0)}px`,
      )
    }
    tooltip.style.setProperty('opacity', `${position?.show ? '1' : 0}`)
    tooltip.style.setProperty('transition', 'all 300ms ease 0s')
  }, [position, hoverIndex])

  const onMouseEnter = (
    data: HoverData,
    index: number,
    spendType: SpendType,
    currency: string | undefined,
  ): void => {
    analytics?.track('home page chart - hover', {
      month:
        data?.payload?.month === props.graphData.length
          ? 'current'
          : `month${props.graphData.length + 1 - (data.payload?.month || 0)}`,
    })
    setSelectedSpendType(spendType)
    setSelectedCurrency(currency)
    setPosition({ data: data, show: true })
    setHoverIndex(index)
  }

  const onMouseLeave = (data: HoverData): void => {
    setSelectedSpendType(SpendType.none)
    setPosition({ data: data, show: false })
    setHoverIndex(-1)
  }

  const adjustedGraphData: SpendGraphData[] = props.graphData.map((data) => {
    const updated = {
      ...data,
      selectedSpendType: spendType,
      selectedCurrency: selectedCurrency,
    }
    return updated
  })

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <ResponsiveContainer
        css={css`
          .recharts-cartesian-axis-tick-value {
            ${getFontCss(
              12,
              styleConstants.colors.gray_dark,
              styleConstants.fontFamilies.regular,
            )};
          }

          .recharts-surface {
            overflow: visible;
          }

          .recharts-cartesian-axis-tick {
            line {
              display: none;
            }
          }

          .recharts-cartesian-grid-horizontal {
            line {
              opacity: 0.5;
            }
          }

          .recharts-label {
            tspan {
              ${getFontCss(
                12,
                styleConstants.colors.gray_dark,
                styleConstants.fontFamilies.regular,
              )};
              fill: ${styleConstants.colors.gray_dark};
            }
          }
        `}
        width={'100%'}
        height={props.height || 300}
      >
        <BarChart data={adjustedGraphData} barCategoryGap={'20%'} barGap={10}>
          {props.defs}
          <CartesianGrid strokeDasharray={'4'} vertical={false} />
          <XAxis
            tick={props.xAxisProps?.ticks}
            label={props.xAxisProps?.label}
            dataKey={'x'}
            tickFormatter={props.xAxisProps?.tickFormatter}
            axisLine={{ stroke: styleConstants.colors.gray_medium }}
            domain={props.xAxisProps?.domain}
          />
          <YAxis
            axisLine={false}
            interval={'preserveStartEnd'}
            domain={props.yAxisProps?.domain}
            tickFormatter={props.yAxisProps?.tickFormatter}
            allowDecimals={false}
          />
          {props.tooltip && (
            <Tooltip
              isAnimationActive={false}
              shared={true}
              cursor={false}
              position={{
                // Static position
                x: position?.data.x ?? 0,
                y: position?.data.y ?? 0,
              }}
              content={props.tooltip}
              wrapperStyle={{ outline: 'none' }}
            />
          )}
          {yDataExists && (
            <Bar
              dataKey={'y'}
              cursor={props.onClick && 'pointer'}
              fill={props.barProps?.fill || brandHighlightColor}
              fillOpacity={1}
              radius={props.barProps?.radius}
              barSize={props.barProps?.size}
              onMouseEnter={(data: HoverData, index: number): void => {
                onMouseEnter(data, index, SpendType.none, undefined)
              }}
              onMouseOut={(data): void => {
                setPosition({ data: data, show: false })
                setHoverIndex(-1)
              }}
              onClick={(data): void => props.onClick?.(data)}
            />
          )}
          {usdDataExists &&
            spendBarStack({
              currency: 'usd',
              graphData: props.graphData,
              barProps: props.barProps,
              onMouseEnter: onMouseEnter,
              onMouseLeave: onMouseLeave,
              onClick: props.onClick,
              hoverIndex: hoverIndex,
            })}
          {cadDataExists &&
            spendBarStack({
              currency: 'cad',
              graphData: props.graphData,
              barProps: props.barProps,
              onMouseEnter: onMouseEnter,
              onMouseLeave: onMouseLeave,
              onClick: props.onClick,
              hoverIndex: hoverIndex,
            })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default SpendGraph
